/** Hex View — interactive hex editor with selection, inline editing, find/replace, and virtual scroll. */
import React, { useCallback, useEffect,useMemo, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

import { DataTypeInspector } from './DataTypeInspector';
import { type ByteRange,useByteSelection } from './useByteSelection';

const BYTES_PER_ROW = 16, ROW_HEIGHT = 20, OVERSCAN = 20;

function textToBytes(text: string): Uint8Array { return new TextEncoder().encode(text); }
function bytesToText(bytes: Uint8Array): string | null {
  try { return new TextDecoder('utf-8', { fatal: true }).decode(bytes); } catch { return null; }
}
function toHex(byte: number): string { return byte.toString(16).padStart(2, '0'); }
function toOffset(offset: number): string { return offset.toString(16).padStart(8, '0'); }
function toPrintableAscii(byte: number): string { return byte >= 0x20 && byte <= 0x7e ? String.fromCharCode(byte) : '.'; }

function parseHexPattern(query: string): number[] | null {
  const c = query.replace(/\s+/g, '');
  if (!c.length || c.length % 2 !== 0 || !/^[0-9a-fA-F]+$/.test(c)) return null;
  const r: number[] = [];
  for (let i = 0; i < c.length; i += 2) r.push(parseInt(c.substring(i, i + 2), 16));
  return r;
}

function findPatternMatches(haystack: Uint8Array, needle: number[]): number[] {
  const starts: number[] = [];
  if (!needle.length || needle.length > haystack.length) return starts;
  outer: for (let i = 0; i <= haystack.length - needle.length; i++) {
    for (let j = 0; j < needle.length; j++) if (haystack[i + j] !== needle[j]) continue outer;
    starts.push(i);
  }
  return starts;
}

function byteHighlight(byteIdx: number, sel: ByteRange | null, matchSet: Set<number>,
  matchStarts: number[], currentMatchIdx: number, patternLen: number): string {
  const isCurrent = matchStarts.length > 0 && byteIdx >= matchStarts[currentMatchIdx] && byteIdx < matchStarts[currentMatchIdx] + patternLen;
  if (isCurrent) return 'bg-orange-400/60 text-foreground font-semibold';
  if (matchSet.has(byteIdx)) return 'bg-yellow-300/40 text-foreground';
  if (sel && byteIdx >= sel.start && byteIdx <= sel.end) {
    return sel.start === sel.end
      ? 'bg-primary/20 text-primary font-semibold'
      : 'bg-blue-200 dark:bg-blue-800 text-foreground';
  }
  return '';
}

interface HexViewProps { data: string; editMode?: boolean; onEdit?: (newData: string) => void; }

export const HexView: React.FC<HexViewProps> = ({ data, editMode, onEdit }) => {
  const bytes = useMemo(() => textToBytes(data), [data]);
  const rows = useMemo(() => {
    const r: { offset: number; bytes: number[] }[] = [];
    for (let i = 0; i < bytes.length; i += BYTES_PER_ROW)
      r.push({ offset: i, bytes: Array.from(bytes.slice(i, i + BYTES_PER_ROW)) });
    return r;
  }, [bytes]);

  const { selection, setSelection, handlePointerDown, handlePointerMove, handlePointerUp } = useByteSelection();

  const [editingByte, setEditingByte] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editError, setEditError] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMode, setSearchMode] = useState<'hex' | 'ascii'>('hex');
  const [currentMatchIdx, setCurrentMatchIdx] = useState(0);
  const [replaceValue, setReplaceValue] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewHeight, setViewHeight] = useState(600);
  const editInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (!editMode) { setEditingByte(null); setEditError(false); } }, [editMode]);

  const { matchStarts, matchSet, patternLen } = useMemo(() => {
    const empty = { matchStarts: [] as number[], matchSet: new Set<number>(), patternLen: 0 };
    if (!searchQuery.trim()) return empty;
    let pattern: number[] | null = searchMode === 'hex' ? parseHexPattern(searchQuery) : null;
    if (searchMode === 'ascii') { const enc = textToBytes(searchQuery); if (enc.length) pattern = Array.from(enc); }
    if (!pattern) return empty;
    const starts = findPatternMatches(bytes, pattern);
    const set = new Set<number>();
    for (const s of starts) for (let i = 0; i < pattern.length; i++) set.add(s + i);
    return { matchStarts: starts, matchSet: set, patternLen: pattern.length };
  }, [bytes, searchQuery, searchMode]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const obs = new ResizeObserver((e) => { const h = e[0]?.contentRect.height; if (h) setViewHeight(h); });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => { setScrollTop(e.currentTarget.scrollTop); }, []);

  const scrollToByte = useCallback((byteIdx: number) => {
    const top = Math.floor(byteIdx / BYTES_PER_ROW) * ROW_HEIGHT - viewHeight / 2 + ROW_HEIGHT;
    scrollRef.current?.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }, [viewHeight]);

  /** Enter edit mode for a single byte (only on confirmed click, not drag). */
  const startEditing = useCallback((byteIdx: number) => {
    if (!editMode || !onEdit) return;
    setEditingByte(byteIdx); setEditValue(toHex(bytes[byteIdx])); setEditError(false);
    setTimeout(() => editInputRef.current?.select(), 0);
  }, [editMode, onEdit, bytes]);

  /** Pointer-up handler: finalize selection and optionally start editing. */
  const onPointerUp = useCallback((e: React.PointerEvent) => {
    const wasClick = handlePointerUp(e);
    if (wasClick && editMode && onEdit && selection?.start === selection?.end && selection !== null) {
      startEditing(selection.start);
    }
  }, [handlePointerUp, editMode, onEdit, selection, startEditing]);

  const commitEdit = useCallback((byteIdx: number, hexVal: string) => {
    if (!onEdit || !/^[0-9a-fA-F]{2}$/.test(hexVal)) { setEditError(true); return; }
    const modified = new Uint8Array(bytes);
    modified[byteIdx] = parseInt(hexVal, 16);
    const text = bytesToText(modified);
    if (text === null) { setEditError(true); return; }
    onEdit(text); setEditingByte(null); setEditError(false);
    if (byteIdx + 1 < bytes.length) {
      setSelection(byteIdx + 1);
      setEditingByte(byteIdx + 1); setEditValue(toHex(bytes[byteIdx + 1]));
      setTimeout(() => editInputRef.current?.select(), 0);
    }
  }, [onEdit, bytes, setSelection]);

  const handleEditKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && editingByte !== null) commitEdit(editingByte, editValue);
    else if (e.key === 'Escape') { setEditingByte(null); setEditError(false); }
  }, [editingByte, editValue, commitEdit]);

  const handleEditChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.toLowerCase();
    setEditValue(val); setEditError(false);
    if (val.length === 2 && /^[0-9a-f]{2}$/.test(val) && editingByte !== null) commitEdit(editingByte, val);
  }, [editingByte, commitEdit]);

  const goToMatch = useCallback((idx: number) => {
    if (!matchStarts.length) return;
    const w = ((idx % matchStarts.length) + matchStarts.length) % matchStarts.length;
    setCurrentMatchIdx(w); setSelection(matchStarts[w]); scrollToByte(matchStarts[w]);
  }, [matchStarts, scrollToByte, setSelection]);

  const handleReplace = useCallback(() => {
    if (!onEdit || !editMode || !matchStarts.length || !patternLen) return;
    let rb: number[] | null = searchMode === 'hex' ? parseHexPattern(replaceValue) : null;
    if (searchMode === 'ascii') { const enc = textToBytes(replaceValue); if (enc.length) rb = Array.from(enc); }
    if (!rb || rb.length !== patternLen) return;
    const modified = new Uint8Array(bytes);
    for (let i = 0; i < rb.length; i++) modified[matchStarts[currentMatchIdx] + i] = rb[i];
    const text = bytesToText(modified);
    if (text !== null) onEdit(text);
  }, [onEdit, editMode, matchStarts, currentMatchIdx, patternLen, replaceValue, searchMode, bytes]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if ((e.ctrlKey || e.metaKey) && e.key === 'f') { e.preventDefault(); setShowSearch(true); } };
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h);
  }, []);

  const totalHeight = rows.length * ROW_HEIGHT;
  const startIdx = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const endIdx = Math.min(rows.length, Math.ceil((scrollTop + viewHeight) / ROW_HEIGHT) + OVERSCAN);

  if (!bytes.length) return <div className="flex items-center justify-center h-full text-sm text-muted-foreground p-4">No data to display</div>;

  const btnCls = 'px-1.5 py-0.5 border border-border rounded hover:bg-accent text-xs';
  const inputCls = 'text-xs bg-background border border-border rounded px-2 py-0.5';
  const selLen = selection ? selection.end - selection.start + 1 : 0;

  return (
    <div className="flex flex-col h-full font-mono text-xs leading-5">
      {showSearch && (
        <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border bg-muted/60 flex-wrap">
          <select value={searchMode} onChange={(e) => { setSearchMode(e.target.value as 'hex' | 'ascii'); setCurrentMatchIdx(0); }}
            className="text-xs bg-background border border-border rounded px-1 py-0.5">
            <option value="hex">Hex</option><option value="ascii">ASCII</option>
          </select>
          <input type="text" placeholder={searchMode === 'hex' ? '48 65 6c 6c 6f' : 'Hello'} value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setCurrentMatchIdx(0); }} className={cn(inputCls, 'flex-1 min-w-[8ch]')} autoFocus />
          <span className="text-[10px] text-muted-foreground whitespace-nowrap">{matchStarts.length ? `${currentMatchIdx + 1}/${matchStarts.length}` : '0/0'}</span>
          <button onClick={() => goToMatch(currentMatchIdx - 1)} className={btnCls} title="Previous">▲</button>
          <button onClick={() => goToMatch(currentMatchIdx + 1)} className={btnCls} title="Next">▼</button>
          {editMode && onEdit && (<>
            <input type="text" placeholder="Replace (same length)" value={replaceValue}
              onChange={(e) => setReplaceValue(e.target.value)} className={cn(inputCls, 'w-28')} />
            <button onClick={handleReplace} className={btnCls} title="Replace current match (same byte length only)">Replace</button>
          </>)}
          <button onClick={() => { setShowSearch(false); setSearchQuery(''); setReplaceValue(''); }}
            className="px-1.5 py-0.5 text-muted-foreground hover:text-foreground text-xs" title="Close">✕</button>
        </div>
      )}
      <div className="flex gap-4 px-3 py-1.5 border-b border-border bg-muted/80 text-muted-foreground select-none backdrop-blur-sm">
        <span className="w-[7ch] shrink-0 text-right">Offset</span>
        <span className="w-[49ch] shrink-0">{Array.from({ length: BYTES_PER_ROW }, (_, i) =>
          toHex(i).toUpperCase() + (i < BYTES_PER_ROW - 1 ? (i === 7 ? '  ' : ' ') : '')).join('')}</span>
        <span className="flex-1 min-w-[18ch]">ASCII</span>
      </div>
      <div ref={scrollRef} onScroll={handleScroll}
        onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={onPointerUp}
        className="flex-1 min-h-0 overflow-y-auto select-none cursor-text">
        <div className="relative" style={{ height: totalHeight }}>
          {rows.slice(startIdx, endIdx).map((row, vi) => (
            <div key={row.offset}
              style={{ position: 'absolute', top: (startIdx + vi) * ROW_HEIGHT, height: ROW_HEIGHT, left: 0, right: 0 }}
              className={cn('flex gap-4 px-3 items-center', row.offset % (BYTES_PER_ROW * 2) === 0 ? 'bg-transparent' : 'bg-muted/20')}>
              <span className="w-[7ch] shrink-0 text-right text-primary/70 select-none">{toOffset(row.offset)}</span>
              <span className="w-[49ch] shrink-0 text-foreground whitespace-pre">
                {Array.from({ length: BYTES_PER_ROW }, (_, i) => {
                  const bi = row.offset + i;
                  const sep = i < BYTES_PER_ROW - 1 ? (i === 7 ? '  ' : ' ') : '';
                  if (i >= row.bytes.length) return <React.Fragment key={i}><span className="text-muted-foreground/30">{'  '}</span>{sep}</React.Fragment>;
                  if (editingByte === bi) return (<React.Fragment key={i}>
                    <input ref={editInputRef} value={editValue} onChange={handleEditChange} onKeyDown={handleEditKeyDown}
                      onBlur={() => { setEditingByte(null); setEditError(false); }} maxLength={2}
                      className={cn('w-[2.2ch] bg-background border rounded text-center text-xs p-0 inline-block outline-none focus:ring-0', editError ? 'border-red-500' : 'border-primary')} autoFocus />{sep}
                  </React.Fragment>);
                  const hl = byteHighlight(bi, selection, matchSet, matchStarts, currentMatchIdx, patternLen);
                  return (<React.Fragment key={i}>
                    <span data-byte-index={bi} className={cn(hl)}>{toHex(row.bytes[i])}{sep}</span>
                  </React.Fragment>);
                })}
              </span>
              <span className="flex-1 min-w-[18ch] tracking-wider">
                {row.bytes.map((b, i) => {
                  const bi = row.offset + i;
                  const hl = byteHighlight(bi, selection, matchSet, matchStarts, currentMatchIdx, patternLen);
                  return (<span key={i} data-byte-index={bi} className={cn(hl,
                    b >= 0x20 && b <= 0x7e ? 'text-foreground' : 'text-muted-foreground/40')}>{toPrintableAscii(b)}</span>);
                })}
              </span>
            </div>
          ))}
        </div>
      </div>
      <DataTypeInspector bytes={bytes} selection={selection} />
      <div className="flex items-center justify-between px-3 py-1 border-t border-border bg-muted/80 text-muted-foreground text-[10px] select-none backdrop-blur-sm">
        <span>{bytes.length.toLocaleString()} bytes</span>
        <div className="flex gap-3">
          {selection && selLen === 1 && <span>Offset: 0x{toOffset(selection.start)} ({selection.start})</span>}
          {selection && selLen > 1 && <span>{selLen} bytes selected (0x{toOffset(selection.start)}–0x{toOffset(selection.end)})</span>}
          {!showSearch && <button onClick={() => setShowSearch(true)} className="hover:text-foreground" title="Find (Ctrl+F)">🔍 Find</button>}
        </div>
        <span>{rows.length.toLocaleString()} rows</span>
      </div>
    </div>
  );
};
